import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import moment from 'moment';
import { DayPickerRangeController, FocusedInputShape, isInclusivelyAfterDay } from 'react-dates';
import 'react-dates/initialize';
import Arrow from '../../common/Icons/Arrow';
import tailwindConfig from '../../../../tailwind.config';
import { ContentType, DISPLAY_DATE_FORMAT, SearchStatus } from '../../../core/constants';
import Button from '../../common/Button';
import Calender from '../../common/Icons/Calender';
import analytics from '../../../core/analytics/analytics';
import SkipTranslationWrapper from '../../common/SkipTranslationWrapper';
import SkipTranslationText from '../../common/SkipTranslationText';

type CheckInAndOutDates = {
  checkInDate: moment.Moment | null;
  checkOutDate: moment.Moment | null;
};

type Props = {
  checkInAndOutDates: CheckInAndOutDates;
  setCheckInAndOutDates: (dates: CheckInAndOutDates) => void;
  checkInDateLimit?: moment.Moment;
  searchStatus?: SearchStatus;
  setSearchStatus: (status: SearchStatus) => void;
};

const DatePicker = ({
  checkInAndOutDates,
  setCheckInAndOutDates,
  checkInDateLimit,
  searchStatus,
  setSearchStatus,
}: Props) => {
  const { t } = useTranslation();
  const [focusedInput, setFocusedInput] = useState<FocusedInputShape | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [datePickerDate, setDatePickerDate] = useState<CheckInAndOutDates>(checkInAndOutDates);
  const dateLimit = checkInDateLimit || moment().add(12, 'months');

  // update check in and out dates if selecting date is actually finish to avoid unnecessary api request
  const updateCheckInAndOutDates = ({ checkInDate, checkOutDate }: CheckInAndOutDates) => {
    setDatePickerDate({ checkInDate, checkOutDate });
    setCheckInAndOutDates({ checkInDate, checkOutDate });
  };

  useEffect(() => {
    setDatePickerDate(checkInAndOutDates);
  }, [checkInAndOutDates, setDatePickerDate]);

  useEffect(() => {
    if (searchStatus === SearchStatus.DATE) {
      setFocusedInput('startDate');
    }
  }, [searchStatus]);

  const events = {
    onMobileFocusChange: (input: FocusedInputShape | null) => {
      setFocusedInput(!input ? 'startDate' : input);
    },
    onConfirm: () => {
      const checkOutDate =
        datePickerDate.checkOutDate || datePickerDate.checkInDate?.clone().add(1, 'days') || null;

      updateCheckInAndOutDates({ ...datePickerDate, checkOutDate });
      setSearchStatus(SearchStatus.GUEST);
    },
    onClickButton: () => {
      setSearchStatus(SearchStatus.DATE);
      setFocusedInput('startDate');
    },
  };

  const Footer = useCallback(
    () => (
      <div className="footer flex items-center justify-between border-t border-solid border-gray-200 bg-white px-[30px] py-3">
        {errorMessage ? (
          <p className="footer-error-text text-small">{errorMessage}</p>
        ) : (
          <p className="footer-days-count-text text-small">
            <SkipTranslationText
              i18nKey="hotel:availability.date_picker.stay_nights"
              values={{
                stayNights:
                  datePickerDate.checkOutDate && datePickerDate.checkInDate
                    ? Math.ceil(
                        // hack to include a day which is less than 24 hours: https://github.com/moment/moment/issues/3713
                        datePickerDate.checkOutDate?.diff(datePickerDate.checkInDate, 'days', true)
                      )
                    : 0,
              }}
            />
          </p>
        )}
        <Button
          disabled={Boolean(errorMessage)}
          size="small"
          color="red"
          onClick={events.onConfirm}
        >
          {t('common:button.apply')}
        </Button>
      </div>
    ),
    [errorMessage, datePickerDate.checkOutDate, datePickerDate.checkInDate, t, events.onConfirm]
  );

  return searchStatus === SearchStatus.DATE ? (
    <div className="flex h-full flex-col rounded-3xl bg-white pt-4">
      <div className="flex h-7 items-center justify-center">
        <p className="text-xl font-bold">{t('component:date_picker.modal.title')}</p>
      </div>
      <div className="flex grow flex-col">
        <DayPickerRangeController
          startDate={datePickerDate.checkInDate}
          endDate={datePickerDate.checkOutDate}
          initialVisibleMonth={() => moment()}
          numberOfMonths={13}
          onFocusChange={(f) => events.onMobileFocusChange(f)}
          focusedInput={focusedInput}
          orientation="verticalScrollable"
          navPrev={<div />}
          navNext={<div />}
          hideKeyboardShortcutsPanel
          renderDayContents={(day) => (
            <p className="day-contents flex size-full items-center justify-center rounded-full text-regular">
              {day.format('D')}
            </p>
          )}
          onDatesChange={({ startDate, endDate }) => {
            if (startDate && focusedInput === 'startDate') {
              setErrorMessage('');
              setDatePickerDate({ checkInDate: startDate, checkOutDate: null });
            } else if (endDate && Math.round(endDate.diff(startDate, 'days', true)) > 28) {
              // if stay nights selected are more than 28 nights, show an error message and keep it open
              setErrorMessage(t('component:date_picker.error.message'));
              setDatePickerDate({ checkInDate: startDate, checkOutDate: null });
            } else if (startDate && endDate && focusedInput === 'endDate') {
              // if checkInDate and checkOutDate are both set, close it
              setErrorMessage('');
              setFocusedInput(null); // setting focusedInput null closes the date picker
              updateCheckInAndOutDates({ checkInDate: startDate, checkOutDate: endDate });
              analytics.google.basic.track.select(
                ContentType.DATE_PICKER,
                `${startDate.toISOString()} - ${endDate.toISOString()}`
              );
            } else {
              // else keep it open
              setErrorMessage('');
              setDatePickerDate({ checkInDate: startDate, checkOutDate: endDate });
            }
          }}
          isOutsideRange={(day) => {
            const isDateBeforeToday = !isInclusivelyAfterDay(day, moment()); // default react-dates condition
            const isDateAfterLimit = day.isAfter(dateLimit);

            return isDateBeforeToday || isDateAfterLimit;
          }}
        />
      </div>
      {Footer()}
    </div>
  ) : (
    <div>
      <button
        className="flex h-14 w-full cursor-pointer items-center gap-4 rounded-full bg-white px-5"
        type="button"
        onClick={events.onClickButton}
      >
        <div className="flex w-3/6 items-center gap-2">
          <Calender size={24} color={tailwindConfig.theme.colors.orange[700]} />
          <SkipTranslationWrapper tag="p" className="truncate">
            {datePickerDate.checkInDate?.format(DISPLAY_DATE_FORMAT)}
          </SkipTranslationWrapper>
        </div>
        <div className="relative">
          <div className="line-top" />
          <Arrow size={14} color={tailwindConfig.theme.colors.orange[700]} />
          <div className="line-bottom" />
        </div>
        <div className="flex w-3/6 items-center gap-4 overflow-x-hidden">
          <SkipTranslationWrapper tag="p" className="truncate">
            {datePickerDate.checkOutDate?.format(DISPLAY_DATE_FORMAT)}
          </SkipTranslationWrapper>
        </div>
      </button>
    </div>
  );
};

export default DatePicker;
